import { CommonModule } from "@angular/common";
import { Component, computed, signal } from "@angular/core";
import { ColDef, GridOptions } from "ag-grid-community";

// EZ
import { EzBaseComponent } from "@template-ui/components";
import { DocMeta } from "@template-ui/decorators";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import {
  CrudInlineSaveCancelButtonDisplay,
  EzFormInputType,
  EzGridColTypes,
  EzGridDsConfig,
  InputConfig,
  PanelHeaderConfig
} from "@template-ui/types";

type StarWarsActor = {
  id: string;
  firstName: string;
  lastName: string;
  biography: string;
  trilogy: string;
  isLiving: boolean;
};

type SampleCreateButtonLocation = "none" | "belowGrid" | "panelHeader";

@DocMeta({
  hide: true,
  category: "Components",
  showcaseTitle: "Edit modes",
  showcaseDesc: "Demonstrates inline row editing with Save/Cancel buttons and a custom popup textarea editor.",
  showcaseAttachTo: [{ libName: "Base Library", classNames: ["EzGridComponent"] }],
})
@Component({
  selector: "ui-grid-in-cell-editing-sample, ez-grid-in-cell-editing-sample",
  standalone: true,
  imports: [CommonModule, UiLibraryModule],
  templateUrl: "./ez-grid-in-cell-editing-sample.component.html",
  styleUrl: "./ez-grid-in-cell-editing-sample.component.scss",
})
export class EzGridInCellEditingSampleComponent extends EzBaseComponent {
  protected readonly modeFormValues = signal({
    editMode: "dialog" as "dialog" | "inline-auto" | "inline-button",
    createButtonLocation: "none" as SampleCreateButtonLocation,
    allowDelete: false,
    saveCancelButtonDisplay: "text" as CrudInlineSaveCancelButtonDisplay,
  });

  protected readonly modeFormConfig: InputConfig[] = [
    {
      type: EzFormInputType.SELECT,
      name: "editMode",
      label: "Edit Mode",
      width: "1x",
      options: [
        { viewValue: "Dialog (default)", value: "dialog" },
        { viewValue: "Inline Auto", value: "inline-auto" },
        { viewValue: "Inline Buttons", value: "inline-button" },
      ],
    },
    {
      type: EzFormInputType.SELECT,
      name: "createButtonLocation",
      label: "Add Button",
      width: "1x",
      options: [
        { viewValue: "None", value: "none" },
        { viewValue: "Below Grid", value: "belowGrid" },
        { viewValue: "Panel Header", value: "panelHeader" },
      ],
    },
    {
      type: EzFormInputType.CHECKBOX,
      name: "allowDelete",
      label: "Allow Delete",
      width: "1x",
    },
    {
      type: EzFormInputType.SELECT,
      name: "saveCancelButtonDisplay",
      label: "Inline Save/Cancel",
      width: "1x",
      options: [
        { viewValue: "Text", value: "text" },
        { viewValue: "Icon", value: "icon" },
        { viewValue: "Text + Icon", value: "text+icon" },
      ],
      disabled: computed(() => this.modeFormValues().editMode !== "inline-button"),
    },
  ];

  protected readonly agGridOptions: GridOptions = {
    paginationPageSize: 5,
  };

  protected readonly panelHeaderConfig = computed<PanelHeaderConfig>(() => ({
    panelHeaderTitle: "Star Wars Actors",
    subtitle: this.buildSubtitle(),
    showReloadButton: true,
    hideExportToExcel: true,
  }));

  protected readonly colDefs: ColDef<StarWarsActor>[] = [
    { field: "id", maxWidth: 120 },
    { field: "firstName", headerName: "First Name", type: [EzGridColTypes.EditText], flex: 1 },
    { field: "lastName", headerName: "Last Name", flex: 1 },
    {
      field: "biography",
      headerName: "Biography",
      minWidth: 320,
      flex: 4,
      type: [EzGridColTypes.EditTextArea],
    },
    {
      field: "trilogy",
      headerName: "Trilogy",
      maxWidth: 140,
      type: [EzGridColTypes.EditSelect],
      cellEditorParams: { values: ["original", "prequel", "sequel", "expanded"] },
    },
    { field: "isLiving", headerName: "Is Living", maxWidth: 120 },
  ];

  protected readonly editDialogInputs: InputConfig[] = [
    { type: EzFormInputType.TEXT, name: "firstName", label: "First Name", width: "1x" },
    { type: EzFormInputType.TEXT, name: "lastName", label: "Last Name", width: "1x" },
    { type: EzFormInputType.TEXTAREA, name: "biography", label: "Biography", width: "fill", textAreaRows: 8, maxLength: 4000 },
    {
      type: EzFormInputType.SELECT,
      name: "trilogy",
      label: "Trilogy",
      width: "1x",
      options: [
        { viewValue: "original", value: "original" },
        { viewValue: "prequel", value: "prequel" },
        { viewValue: "sequel", value: "sequel" },
        { viewValue: "expanded", value: "expanded" },
      ],
    },
    { type: EzFormInputType.CHECKBOX, name: "isLiving", label: "Is Living", width: "1x" },
  ];

  protected readonly dsConfig = computed<EzGridDsConfig>(() => ({
    datasourceId: "EZ_GRID_INLINE_BUTTONS_SAMPLE",
    colDefs: this.colDefs,
    bindings: [
      { bindingType: "CREATE", endpoint: "mock/StarWarsActors", protocol: "POST_JSON" },
      { bindingType: "READ", endpoint: "mock/StarWarsActors", protocol: "GET" },
      { bindingType: "UPDATE", endpoint: "mock/StarWarsActors/{id}", protocol: "PUT_JSON" },
      { bindingType: "DELETE", endpoint: "mock/StarWarsActors/{id}", protocol: "DELETE" },
    ],
    crudActions: {
      canEdit: true,
      canCreate: this.modeFormValues().createButtonLocation !== "none",
      canDelete: this.modeFormValues().allowDelete,
      idField: "id",
      editMode: this.modeFormValues().editMode,
      actions: {
        create: {
          autoButton: {
            buttonText: "Add Actor",
            location: this.resolveCreateButtonLocation(),
          },
        },
      },
      formConfig: {
        inputConfig: this.editDialogInputs,
        dialogData: { header: "{{action}} Actor" },
      },
      inlineConfig: {
        saveCancelButtonDisplay: this.modeFormValues().saveCancelButtonDisplay,
        validateChanges: (row, changes) => {
          const updatedFirstName = changes["firstName"];
          if (updatedFirstName !== undefined && !String(updatedFirstName).trim()) {
            return "First Name is required.";
          }
          return undefined;
        },
      },
    },
  }));

  constructor() {
    super();
    this.thisClassName = "EzGridInCellEditingSampleComponent";
  }

  private buildSubtitle(): string {
    const { allowDelete, createButtonLocation, editMode, saveCancelButtonDisplay } = this.modeFormValues();
    const inlineButtonSummary = editMode === "inline-button"
      ? ` | Buttons: ${this.formatInlineButtonDisplay(saveCancelButtonDisplay)}`
      : "";

    return `Mode: ${editMode} | Add: ${this.formatCreateButtonLocation(createButtonLocation)} | Delete: ${allowDelete ? "On" : "Off"}${inlineButtonSummary}`;
  }

  private formatInlineButtonDisplay(display: CrudInlineSaveCancelButtonDisplay): string {
    switch (display) {
      case "icon":
        return "Icon";
      case "text+icon":
        return "Text + Icon";
      default:
        return "Text";
    }
  }

  private formatCreateButtonLocation(location: SampleCreateButtonLocation): string {
    switch (location) {
      case "belowGrid":
        return "Below Grid";
      case "panelHeader":
        return "Panel Header";
      default:
        return "None";
    }
  }

  private resolveCreateButtonLocation(): "belowGrid" | "panelHeader" {
    return this.modeFormValues().createButtonLocation === "panelHeader" ? "panelHeader" : "belowGrid";
  }
}
